import { Landmark, Wallet } from "lucide-react";

import PaginationComponent from "../PaginationComponent";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../utils/components/ui/select";

type BankingTransactionRow = {
  id: number;
  transactionDate: string;
  transactionType: string;
  mode: string;
  category: string;
  referenceId: string;
  cashIn: number;
  cashOut: number;
  remarks: string;
  createdBy: string;
};

type BankingTransactionsTableProps = {
  transactions: BankingTransactionRow[];
  isLoading: boolean;
  totals: {
    cashIn: number;
    cashOut: number;
  };
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  serverPageNumber?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  formatTableDate: (value: string) => string;
  formatCurrency: (value: number) => string;
};

function BankingTransactionsTable({
  transactions,
  isLoading,
  totals,
  totalElements,
  totalPages,
  currentPage,
  pageSize,
  serverPageNumber,
  onPageChange,
  onPageSizeChange,
  formatTableDate,
  formatCurrency,
}: BankingTransactionsTableProps) {
  return (
    <>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-[1100px] w-full divide-y divide-[#EEF2F6]">
          <thead className="sticky top-0 z-10 bg-[#F8FAFC]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Transaction Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Transaction Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Mode
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Category
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Reference ID
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Cash In
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Cash Out
              </th>
              <th className="min-w-[200px] px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Remarks
              </th>
              <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Created By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EEF2F6] bg-white">
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <tr key={transaction.id} className="transition hover:bg-[#FAFCFF]">
                  <td className="px-6 py-5 text-sm text-slate-600">
                    {formatTableDate(transaction.transactionDate)}
                  </td>
                  <td className="px-6 py-5">
                    <span className="inline-flex rounded-full border border-[#E2EDBF] bg-[#F7FCEB] px-3 py-1 text-xs font-semibold tracking-wide text-[#6A9D0A]">
                      {transaction.transactionType}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-1.5 text-sm text-slate-600">
                      {transaction.mode === "Bank" ? (
                        <Landmark className="h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <Wallet className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                      {transaction.mode}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-sm font-medium text-slate-700">
                    {transaction.category}
                  </td>
                  <td className="px-6 py-5 text-sm text-slate-600">
                    {transaction.referenceId}
                  </td>
                  <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                    {transaction.cashIn === 0
                      ? "--"
                      : formatCurrency(transaction.cashIn)}
                  </td>
                  <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                    {transaction.cashOut === 0
                      ? "0"
                      : formatCurrency(transaction.cashOut)}
                  </td>
                  <td className="px-6 py-5 text-sm text-slate-600">
                    {transaction.remarks}
                  </td>
                  <td className="px-6 py-5">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-1.5 text-sm text-slate-600">
                      {transaction.createdBy}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-12 text-center text-sm text-slate-500"
                >
                  {isLoading
                    ? "Loading transactions..."
                    : "No transactions match the selected filters."}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-[#F7F8F0]">
            <tr>
              <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                Total
              </td>
              <td colSpan={4} />
              <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                {formatCurrency(totals.cashIn)}
              </td>
              <td className="px-6 py-5 text-sm font-semibold text-slate-900">
                {formatCurrency(totals.cashOut)}
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

    </>
  );
}

export default BankingTransactionsTable;
